import { hasTeamsSession, markTeamsSession } from './teamsSession';

/** True when the page is running inside a Teams iframe (not a normal browser tab). */
export function isTeamsHost(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    // window.top access throws SecurityError in cross-origin frames and some Electron webviews.
    return window.self !== window.top;
  } catch {
    return false;
  }
}

/** Detect Teams tab at load time (iframe, ?teams=true, or prior Teams session in this tab). */
export function isTeamsEmbeddedOnLoad(): boolean {
  if (typeof window === 'undefined') return false;
  if (isTeamsHost()) {
    markTeamsSession();
    return true;
  }
  // ?teams=true signals Teams context for the SSO popup / deeplink flow, but must NOT
  // permanently mark the session — only a confirmed iframe (isTeamsHost) may do that.
  if (new URLSearchParams(window.location.search).get('teams') === 'true') {
    return true;
  }
  if (hasTeamsSession()) return true;
  return false;
}

/** Broader Teams embedding check (includes DOM markers set after bootstrap). */
export function isTeamsEmbedded(): boolean {
  if (isTeamsEmbeddedOnLoad()) return true;
  if (typeof document === 'undefined') return false;
  if (document.body.classList.contains('teams-embedded')) return true;
  return Boolean(document.querySelector('.teams-integration.in-teams'));
}

/** Strip trailing markdown / punctuation noise from Coveo URLs. */
export function normalizeOpenableUrl(href: string): string | null {
  const trimmed = href.trim().replace(/[)\].,;:'"]+$/g, '');
  if (!trimmed) return null;
  try {
    const url = new URL(trimmed, window.location.href);
    if (!/^https?:$/i.test(url.protocol)) return null;
    return url.href;
  } catch {
    return /^https?:\/\//i.test(trimmed) ? trimmed : null;
  }
}

export function isExternalHttpUrl(href: string): boolean {
  const normalized = normalizeOpenableUrl(href);
  if (!normalized) return false;
  try {
    return new URL(normalized).origin !== window.location.origin;
  } catch {
    return false;
  }
}

export function isOpenableHttpUrl(href: string): boolean {
  return normalizeOpenableUrl(href) !== null;
}
